"use client";

// WayCon photo showreel for the about-us bento portrait tile (WEB-717).
// Blur crossfade reverse-engineered from Jitter "Blur: Article Gallery [iOS]":
// ~1.2s ease-out transition where the outgoing frame blurs + fades while the
// incoming frame de-blurs + fades in; ~3.5s hold per slide before the next swap.

import * as React from "react";

import {
  BLUR_CROSSFADE_BLUR_PX,
  BLUR_CROSSFADE_HIDDEN,
  BLUR_CROSSFADE_LAYER,
  BLUR_CROSSFADE_MS,
  BLUR_CROSSFADE_VISIBLE,
  blurCrossfadeDurationStyle,
} from "@/components/ui/blur-crossfade";
import type { BentoWayConShowreelImage } from "@/content/shared/about-us/bento-waycon-showreel";
import { cn } from "@/lib/utils";

const HOLD_MS = 3500;
const TRANSITION_MS = BLUR_CROSSFADE_MS;

const SHOWREEL_LAYER = cn(
  "absolute inset-0 size-full object-cover",
  BLUR_CROSSFADE_LAYER
);

function preloadImage(src: string): Promise<void> {
  return new Promise((resolve) => {
    const img = new window.Image();
    const finish = () => resolve();
    img.addEventListener("load", () => {
      void img.decode?.().then(finish).catch(finish);
    });
    img.addEventListener("error", finish, { once: true });
    img.src = src;
  });
}

interface BentoWayConShowreelProps {
  images: readonly BentoWayConShowreelImage[];
  className?: string;
}

export function BentoWayConShowreel({
  images,
  className,
}: BentoWayConShowreelProps) {
  const [baseIndex, setBaseIndex] = React.useState(0);
  const [overlayIndex, setOverlayIndex] = React.useState<number | null>(null);
  const [overlayVisible, setOverlayVisible] = React.useState(false);
  /** Disables CSS transitions while syncing base/overlay after a crossfade. */
  const [instant, setInstant] = React.useState(false);

  React.useEffect(() => {
    void Promise.all(images.map((image) => preloadImage(image.src)));
  }, [images]);

  React.useEffect(() => {
    if (images.length <= 1) return;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReducedMotion) return;

    let cancelled = false;
    let currentIndex = 0;
    let holdTimeoutId = 0;
    let transitionTimeoutId = 0;

    const settleTransition = (nextIndex: number) => {
      // Overlay still shows the new frame — update the base underneath, then
      // remove the overlay instantly so there is no gap between layers.
      setInstant(true);
      setBaseIndex(nextIndex);

      requestAnimationFrame(() => {
        if (cancelled) return;
        setOverlayVisible(false);
        setOverlayIndex(null);

        requestAnimationFrame(() => {
          if (cancelled) return;
          setInstant(false);
        });
      });
    };

    const startTransition = (nextIndex: number) => {
      void preloadImage(images[nextIndex]?.src ?? "").then(() => {
        if (cancelled) return undefined;

        setOverlayIndex(nextIndex);
        setOverlayVisible(false);

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (cancelled) return;
            setOverlayVisible(true);
          });
        });

        transitionTimeoutId = window.setTimeout(() => {
          if (cancelled) return;
          currentIndex = nextIndex;
          settleTransition(nextIndex);
          scheduleNext();
        }, TRANSITION_MS);

        return undefined;
      });
    };

    const scheduleNext = () => {
      holdTimeoutId = window.setTimeout(() => {
        if (cancelled) return;
        startTransition((currentIndex + 1) % images.length);
      }, HOLD_MS);
    };

    scheduleNext();

    return () => {
      cancelled = true;
      window.clearTimeout(holdTimeoutId);
      window.clearTimeout(transitionTimeoutId);
    };
  }, [images]);

  if (images.length === 0) return null;

  const baseImage = images[baseIndex] ?? images[0]!;
  const overlayImage =
    overlayIndex !== null ? (images[overlayIndex] ?? null) : null;
  const transitionStyle = (objectPosition: string) => ({
    ...blurCrossfadeDurationStyle(instant, false),
    objectPosition,
  });

  return (
    <div
      className={cn("bg-grey-200 absolute inset-0 overflow-hidden", className)}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={baseImage.src}
        alt={baseImage.alt}
        className={cn(SHOWREEL_LAYER, "z-0", BLUR_CROSSFADE_VISIBLE)}
        style={transitionStyle(baseImage.objectPosition)}
        loading="eager"
        decoding="async"
        fetchPriority="high"
      />
      {overlayImage ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={overlayImage.src}
          alt=""
          aria-hidden
          className={cn(
            SHOWREEL_LAYER,
            "z-10",
            overlayVisible ? BLUR_CROSSFADE_VISIBLE : BLUR_CROSSFADE_HIDDEN
          )}
          style={transitionStyle(overlayImage.objectPosition)}
          loading="eager"
          decoding="async"
        />
      ) : null}
      <span className="sr-only" aria-live="polite">
        {baseImage.alt}
      </span>
    </div>
  );
}

export const BENTO_WAYCON_SHOWREEL_TIMING = {
  holdMs: HOLD_MS,
  transitionMs: TRANSITION_MS,
  blurPx: BLUR_CROSSFADE_BLUR_PX,
} as const;
